// E:\tamillearning.ai UI\Tamil-Katral-Maiyam\src\pages\student\AITutorAvatar.tsx

import React, { useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import {
  MessageCircle,
  Sparkles,
  Star,
  Bot,
  PlayCircle,
  Mic,
  Zap,
  Crown,
} from "lucide-react";

type Tutor = {
  id: number;
  name: string;
  avatar: string;
  personality: string;
  specialty: string;
  stars: number;
  color: string;
  phrase: string;
};

function initialFromName(name: string) {
  const clean = (name || "").trim();
  return clean ? clean.charAt(0) : "A";
}

function StarsRow({ value }: { value: number }) {
  const full = Math.floor(value || 0);
  return (
    <div className="flex items-center justify-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-6 h-6 ${
            i < full ? "text-yellow-300 fill-yellow-300" : "text-yellow-200"
          }`}
        />
      ))}
      <span className="text-xl font-bold text-yellow-200">({value})</span>
    </div>
  );
}

/** ✅ Replaces missing Avatar component */
function SafeAvatar({
  src,
  fallback,
  sizeClass,
  ringClass,
  borderClass,
}: {
  src?: string;
  fallback: string;
  sizeClass: string; // e.g. "w-48 h-48"
  ringClass?: string; // e.g. "ring-8 ring-white/30"
  borderClass?: string; // e.g. "border-8 border-white/50"
}) {
  const [broken, setBroken] = useState(false);
  return (
    <div
      className={[
        "rounded-full overflow-hidden flex items-center justify-center shadow-2xl bg-white/20",
        sizeClass,
        borderClass || "",
        ringClass || "",
      ].join(" ")}
    >
      {!broken && src ? (
        <img
          src={src}
          alt="Tutor avatar"
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white font-black text-5xl">
          {fallback}
        </div>
      )}
    </div>
  );
}

export const AITutorAvatarPage: React.FC = () => {
  const [currentTutor, setCurrentTutor] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);

  const tutors: Tutor[] = [
    {
      id: 1,
      name: "அம்மா AI 👩‍🏫",
      avatar: "/avatars/amma-ai.png",
      personality: "Loving & Patient",
      specialty: "Beginner Tamil",
      stars: 5,
      color: "from-pink-400 to-rose-500",
      phrase: "வணக்கம் கண்ணு! தமிழ் கத்துக்கலாமா? 💕",
    },
    {
      id: 2,
      name: "தாத்தா AI 👴",
      avatar: "/avatars/thatha-ai.png",
      personality: "Wise & Funny",
      specialty: "Stories & Poems",
      stars: 5,
      color: "from-orange-400 to-yellow-500",
      phrase: "என்ன ரா பையா! கதை சொல்லட்டுமா? 📖",
    },
    {
      id: 3,
      name: "அண்ணா AI 💪",
      avatar: "/avatars/anna-ai.png",
      personality: "Cool & Energetic",
      specialty: "Games & Quiz",
      stars: 4.8,
      color: "from-emerald-400 to-teal-500",
      phrase: "ரெடியா மாப்ளே? கேம் ஆடலாமா? 🎮",
    },
    {
      id: 4,
      name: "திவ்யா AI 🌟",
      avatar: "/avatars/thivya-ai.png",
      personality: "Smart & Modern",
      specialty: "Grammar & Speaking",
      stars: 4.9,
      color: "from-purple-400 to-indigo-500",
      phrase: "ஹாய்! தமிழ் ஸ்பீக்கிங் ப்ராக்டிஸ் பண்ணலாமா? 🗣️",
    },
  ];

  const selectedTutor = tutors[currentTutor];

  const handleTutorChange = (index: number) => {
    setCurrentTutor(index);
    setShowWelcome(false);
  };

  const handleSpeak = () => {
    setIsSpeaking(true);
    setTimeout(() => setIsSpeaking(false), 2000);
  };

  return (
    <Layout>
      <PageHeader
        title="🤖 Choose Your AI Tutor! 🎓"
        subtitle="Pick your favorite Tamil teacher avatar! 🌟"
      />

      {/* Welcome Animation */}
      {showWelcome && (
        <div className="text-center py-12 mb-8">
          <div className="inline-block animate-bounce mb-8">
            <Bot className="w-24 h-24 text-purple-400 drop-shadow-2xl mx-auto" />
          </div>
          <h2 className="text-4xl font-black bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent mb-6 drop-shadow-lg">
            Meet Your Tamil Super Teachers! ✨
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
            Each AI tutor has special powers! Choose who you want to learn Tamil
            with today! 🚀
          </p>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Selected Tutor Preview */}
          <Card
            className={`w-full shadow-2xl border-4 border-white/50 group hover:scale-105 transition-all duration-500 ${
              selectedTutor
                ? `bg-gradient-to-br ${selectedTutor.color}`
                : "bg-gradient-to-br from-gray-400 to-gray-500"
            }`}
          >
            <CardHeader className="text-center pb-2">
              <div className="relative">
                <SafeAvatar
                  src={selectedTutor?.avatar}
                  fallback={initialFromName(selectedTutor?.name || "A")}
                  sizeClass="w-48 h-48 mx-auto"
                  borderClass="border-8 border-white/50"
                  ringClass="ring-8 ring-white/30 group-hover:ring-white/50 transition-all"
                />

                {isSpeaking && (
                  <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
                    <div className="w-6 h-6 bg-green-400 rounded-full animate-ping" />
                  </div>
                )}
              </div>

              <CardTitle className="text-4xl font-black drop-shadow-2xl mt-4 bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
                {selectedTutor?.name}
              </CardTitle>

              <div className="mt-2 mb-4">
                <StarsRow value={selectedTutor?.stars || 0} />
              </div>
            </CardHeader>

            <CardContent className="text-center pt-0">
              <Badge
                variant="secondary"
                className="text-lg px-6 py-3 bg-white/30 backdrop-blur-sm border-white/50 mb-6 shadow-lg"
              >
                {selectedTutor?.specialty}
              </Badge>

              <p className="text-2xl font-semibold mb-8 px-4 py-4 bg-white/20 backdrop-blur-sm rounded-2xl drop-shadow-lg">
                "{selectedTutor?.phrase}"
              </p>

              <div className="space-y-4 mb-8">
                <Button
                  onClick={handleSpeak}
                  disabled={isSpeaking}
                  className="w-full bg-white/90 hover:bg-white text-purple-600 font-black text-xl py-6 px-8 rounded-3xl shadow-2xl hover:scale-105 transition-all backdrop-blur-sm border-2 border-white/50"
                >
                  <Mic
                    className={`w-8 h-8 mr-3 transition-all ${
                      isSpeaking ? "animate-pulse text-red-500" : ""
                    }`}
                  />
                  {isSpeaking ? "🎤 Listening..." : "🗣️ Start Talking!"}
                </Button>

                <Button
                  variant="outline"
                  className="w-full border-2 border-white/70 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white font-black text-xl py-6 px-8 rounded-3xl shadow-xl hover:scale-105 transition-all"
                >
                  <MessageCircle className="w-8 h-8 mr-3" />
                  💬 Chat Now!
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tutor Selection */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-slate-50 to-slate-100 shadow-xl border-0">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-3 text-gray-800">
                  <Sparkles className="w-8 h-8 text-purple-500" />
                  Choose Your Favorite Teacher!
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {tutors.map((tutor, index) => (
                    <Card
                      key={tutor.id}
                      className={`p-6 cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300 border-4 hover:border-white/50 group ${
                        currentTutor === index
                          ? `ring-4 ring-yellow-400 shadow-2xl scale-105 bg-gradient-to-br ${tutor.color} text-white`
                          : "border-gray-200 hover:border-purple-300 bg-white/70"
                      }`}
                      onClick={() => handleTutorChange(index)}
                    >
                      <div className="flex items-center gap-4">
                        <SafeAvatar
                          src={tutor.avatar}
                          fallback={initialFromName(tutor.name)}
                          sizeClass="w-20 h-20"
                          borderClass="border-4 border-white/50"
                          ringClass="ring-4 ring-white/30 group-hover:ring-yellow-200/50 transition-all"
                        />

                        <div className="flex-1 min-w-0">
                          <h3
                            className={`font-bold text-lg group-hover:scale-105 transition-transform ${
                              currentTutor === index
                                ? "text-yellow-200 drop-shadow-lg"
                                : "text-gray-800"
                            }`}
                          >
                            {tutor.name}
                          </h3>

                          <p
                            className={`text-sm font-semibold ${
                              currentTutor === index
                                ? "text-yellow-100"
                                : "text-gray-600"
                            }`}
                          >
                            {tutor.personality}
                          </p>

                          <div className="flex items-center gap-1 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < Math.floor(tutor.stars)
                                    ? "text-yellow-400 fill-yellow-400"
                                    : "text-yellow-300"
                                }`}
                              />
                            ))}
                          </div>
                        </div>

                        {currentTutor === index && (
                          <Crown className="w-8 h-8 text-yellow-400 animate-pulse" />
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-xl border-0">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Zap className="w-6 h-6" />
                  Your Learning Stats 📊
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 pt-2">
                <div className="text-center p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                  <p className="text-2xl font-black text-yellow-300">127</p>
                  <p className="text-sm font-medium">Words Learned</p>
                </div>
                <div className="text-center p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                  <p className="text-2xl font-black text-yellow-300">42</p>
                  <p className="text-sm font-medium">Sessions</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Final CTA */}
        <Card className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white shadow-2xl border-0 text-center overflow-hidden">
          <CardContent className="py-16 px-8">
            <Bot className="w-24 h-24 mx-auto mb-8 text-yellow-300 animate-bounce drop-shadow-2xl" />
            <h2 className="text-4xl lg:text-5xl font-black mb-6 bg-gradient-to-r from-yellow-400 to-orange-300 bg-clip-text text-transparent drop-shadow-2xl">
              Ready to Learn Tamil? 🚀
            </h2>
            <p className="text-xl lg:text-2xl mb-12 font-semibold max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
              Your AI tutor is waiting! Click "Start Talking" and begin your Tamil
              adventure! 🌟
            </p>

            <Button
              size="lg"
              className="bg-white text-purple-600 font-black text-2xl py-8 px-16 rounded-3xl shadow-2xl hover:scale-110 transition-all duration-300 backdrop-blur-sm border-4 border-white/50 hover:border-yellow-300"
              onClick={() => window.open("/student/conversation", "_self")}
            >
              <PlayCircle className="w-10 h-10 mr-4" />
              🎓 Start Learning with {selectedTutor?.name}!
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AITutorAvatarPage;
